import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useWebSocket } from './hooks/useWebSocket';
import { useAudioStreaming } from './hooks/useAudioStreaming';
import { MarkdownRenderer } from './components/MarkdownRenderer';
import CostAnalysis from './components/CostAnalysis';
import TrackUserActivity from './components/TrackUserActivity';
import GoogleLogin from './components/GoogleLogin';
import ImplicitCheckerReplay from './components/ImplicitCheckerReplay';
import {
  ChatMessage,
  WebSocketMessage,
  User,
  ConversationMessage,
  PromptPackage,
  PromptPackageUpdate,
} from './types';
import PromptPackages from './components/PromptPackages';
import './App.css';
import './components/ConversationHistory.css';

import { getUserFromToken, logout, setToken } from './services/user';
import { apiClient, PLAYGROUND } from './services/apiClient';
import { Auth, cookieUtils } from './utils';

type TabType =
  | 'cost-analysis'
  | 'live-chat'
  | 'track-users'
  | 'prompt-packages'
  | 'implicit-checker-replay';

const TAB_ROUTE_MAP: Record<TabType, string> = {
  'live-chat': 'session-replay',
  'cost-analysis': 'cost-analysis',
  'track-users': 'track-users',
  'prompt-packages': 'prompt-packages',
  'implicit-checker-replay': 'implicit-checker-replay',
};

const ROUTE_TAB_MAP: Record<string, TabType> = Object.entries(TAB_ROUTE_MAP).reduce(
  (acc, [tab, slug]) => {
    acc[slug] = tab as TabType;
    return acc;
  },
  {} as Record<string, TabType>
);

const normalizePathname = (path: string) => {
  if (!path) return '/';
  const trimmed = path.replace(/\/+$/, '');
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const normalizeBasePath = (path: string) => {
  if (!path || path === '/') return '';
  const trimmed = path.replace(/\/+$/, '');
  if (!trimmed) return '';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const getTabFromPath = (pathname: string): TabType | null => {
  const normalized = normalizePathname(pathname);
  if (normalized === '/') return null;
  const segments = normalized.split('/').filter(Boolean);
  if (!segments.length) return null;
  const slug = segments[segments.length - 1];
  return ROUTE_TAB_MAP[slug] || null;
};

const computeBasePath = (pathname: string): string => {
  const normalized = normalizePathname(pathname);
  if (normalized === '/') return '';
  const segments = normalized.split('/').filter(Boolean);
  if (!segments.length) return '';
  const slug = segments[segments.length - 1];
  if (ROUTE_TAB_MAP[slug]) {
    const baseSegments = segments.slice(0, -1);
    return baseSegments.length ? `/${baseSegments.join('/')}` : '';
  }
  return normalizeBasePath(normalized);
};

const buildPathForTab = (tab: TabType, basePath: string): string => {
  const slug = TAB_ROUTE_MAP[tab];
  const normalizedBase = normalizeBasePath(basePath);
  if (!slug) {
    return normalizedBase || '/';
  }
  return normalizedBase ? `${normalizedBase}/${slug}` : `/${slug}`;
};

const pathsMatch = (pathA: string, pathB: string) =>
  normalizePathname(pathA) === normalizePathname(pathB);

type ReplayTurn = {
  id: string;
  text: string;
  questionUrl?: string;
  solutionUrl?: string;
  boardText?: string | null;
  timestamp?: string | number;
  conversationIndex: number;
};

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

if (!SERVER_URL) {
  console.error('REACT_APP_SERVER_URL environment variable is not set');
}

const getPromptUpdatedTime = (pkg: PromptPackage) => {
  const value = pkg?.metadata?.lastUpdated;
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const sortPromptPackages = (pkgs: PromptPackage[]) =>
  [...pkgs].sort((a, b) => getPromptUpdatedTime(b) - getPromptUpdatedTime(a));

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    if (typeof window === 'undefined') {
      return 'live-chat';
    }
    return getTabFromPath(window.location.pathname) ?? 'live-chat';
  });
  const basePathRef = useRef<string>(
    typeof window === 'undefined' ? '' : computeBasePath(window.location.pathname)
  );
  const hasPathTabRef = useRef<boolean>(
    typeof window === 'undefined' ? false : Boolean(getTabFromPath(window.location.pathname))
  );
  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
    if (typeof window === 'undefined') {
      return;
    }
    const nextPath = buildPathForTab(tab, basePathRef.current);
    hasPathTabRef.current = true;
    basePathRef.current = computeBasePath(nextPath);
    if (pathsMatch(window.location.pathname, nextPath)) {
      return;
    }
    const search = window.location.search || '';
    const hash = window.location.hash || '';
    window.history.pushState({ tab }, '', `${nextPath}${search}${hash}`);
  }, [basePathRef, hasPathTabRef]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [questionImageUrl, setQuestionImageUrl] = useState('');
  const [solutionImageUrl, setSolutionImageUrl] = useState('');
  const [attemptIdInput, setAttemptIdInput] = useState('');
  const [loadedAttemptId, setLoadedAttemptId] = useState<string | null>(null);
  const [isLoadingAttempt, setIsLoadingAttempt] = useState(false);
  const [attemptConversation, setAttemptConversation] = useState<ConversationMessage[]>([]);
  const [replayQueue, setReplayQueue] = useState<ReplayTurn[]>([]);
  const [awaitingReplayResponse, setAwaitingReplayResponse] = useState(false);
  const [currentReplayIndex, setCurrentReplayIndex] = useState(-1);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState<{[key: string]: string}>({});
  const [promptPackages, setPromptPackages] = useState<PromptPackage[]>([]);
  const [selectedPromptPackage, setSelectedPromptPackage] = useState<PromptPackage | null>(null);
  const [isPromptPackagesLoading, setIsPromptPackagesLoading] = useState(false);
  const [isPromptPackagesRefreshing, setIsPromptPackagesRefreshing] = useState(false);
  const [isCreatingPromptPackage, setIsCreatingPromptPackage] = useState(false);
  const [isTutorThinking, setIsTutorThinking] = useState(false);
  const [tutorThinkingStartTime, setTutorThinkingStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [user, setUser] = useState<User | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string | null>(null);
  // Top-level redirect straight to platform-node's OAuth endpoint
  // (mirrors ai-canvas). platform-node bounces the browser back to our
  // origin with ?token=<jwt>; we capture it in the useEffect below.
  const [googleLoginUrl] = useState(`${process.env.REACT_APP_PLATFORM_URL}/auth/google?redirect=${window.location.origin}`);
  const [selectedLiveChatPromptPackage, setSelectedLiveChatPromptPackage] = useState<PromptPackage | null>(null);

  // Initialize environment from sessionStorage (per-tab storage)
  // Using sessionStorage instead of localStorage ensures each tab has independent environment
  const [environment, setEnvironment] = useState<'local' | 'staging' | 'production'>(() => {
    const stored = sessionStorage.getItem('fermi-environment');
    if (stored && ['local', 'staging', 'production'].includes(stored)) {
      return stored as 'local' | 'staging' | 'production';
    }
    return 'production'; // Default environment
  });

  // Persist environment to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('fermi-environment', environment);
  }, [environment]);

  // 401 — token missing/invalid/expired. Clear the client-side cookie and
  // drop user state so the login modal reappears.
  useEffect(() => {
    apiClient.setUnauthorizedHandler((status, code, message) => {
      console.warn('[auth] 401 received', { code, message });
      cookieUtils.delete(Auth.TOKEN);
      cookieUtils.deleteV2(Auth.TOKEN);
      setUser(null);
      setAccessDenied(false);
      setAccessDeniedMessage(null);
    });
  }, []);

  // Set up API client forbidden handler (for 403 errors - email not whitelisted)
  useEffect(() => {
    apiClient.setForbiddenHandler(() => {
      setAccessDenied(true);
      setAccessDeniedMessage('Your email is not authorized to access this application.');
      // Do NOT clear user or token - just show access denied modal
    });
  }, []);


  const replayStatusLabel = replayQueue.length === 0
    ? 'Idle'
    : (awaitingReplayResponse ? 'Awaiting tutor response' : 'Ready to send recorded turns');

  const replayIndexByConversation = useMemo(() => {
    const map = new Map<number, number>();
    replayQueue.forEach((turn, index) => {
      map.set(turn.conversationIndex, index);
    });
    return map;
  }, [replayQueue]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hasPathTabRef.current) return;
    const desiredPath = buildPathForTab(activeTab, basePathRef.current);
    if (!pathsMatch(window.location.pathname, desiredPath)) {
      const search = window.location.search || '';
      const hash = window.location.hash || '';
      window.history.replaceState({ tab: activeTab }, '', `${desiredPath}${search}${hash}`);
    }
    hasPathTabRef.current = true;
  }, [activeTab, basePathRef, hasPathTabRef]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      const { pathname } = window.location;
      basePathRef.current = computeBasePath(pathname);
      const tabFromPath = getTabFromPath(pathname);
      if (tabFromPath) {
        hasPathTabRef.current = true;
        setActiveTab(tabFromPath);
        return;
      }
      const fallbackTab: TabType = 'live-chat';
      hasPathTabRef.current = true;
      const fallbackPath = buildPathForTab(fallbackTab, basePathRef.current);
      if (!pathsMatch(pathname, fallbackPath)) {
        const search = window.location.search || '';
        const hash = window.location.hash || '';
        window.history.replaceState({ tab: fallbackTab }, '', `${fallbackPath}${search}${hash}`);
      }
      setActiveTab(fallbackTab);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [basePathRef, hasPathTabRef]);


  // ai-canvas-style mount flow: if platform-node just redirected us here
  // with ?token=<jwt>, save it to the cookie and clean the URL. Then either
  // way, fetch the user and verify access.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
    }

    getUserFromToken()
      .then(async (user) => {
        if (user && user.id) {
          setUser(user);
          try {
            await apiClient.get('/api/verify-access', PLAYGROUND);
          } catch (error: any) {
            setAccessDenied(true);
            setAccessDeniedMessage(error.message || 'Your email is not authorized.');
          }
        } else {
          setUser(null);
        }
      })
      .catch((error) => {
        console.error('Error fetching user:', error);
        setUser(null);
      });
  }, []);


  const {
    isConnected,
    isConnecting,
    lastMessage,
    connect,
    disconnect,
    sendText,
    sendTutorRequest,
    sendAudio,
    stopAudioStream
  } = useWebSocket();

  const {
    isRecording,
    isMuted,
    isPlaying,
    startRecording,
    stopRecording,
    mute,
    unmute,
    playAudioChunk
  } = useAudioStreaming();

  const disableRecordedSend = !isConnected || awaitingReplayResponse;

  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    console.log(logEntry);
    setLogs(prev => [...prev.slice(-50), logEntry]);
  }, []);

  useEffect(() => {
    if (!isConnected && awaitingReplayResponse) {
      addLog('⚠️ Tutor connection lost; cancelling pending replay turn');
      setAwaitingReplayResponse(false);
    }
  }, [isConnected, awaitingReplayResponse, addLog]);

  useEffect(() => {
    const initializeTiktoken = async () => {
      try {
        // const model = await load(registry[models['o3'] as keyof typeof registry]);
        addLog('✅ Tiktoken initialized successfully');
      } catch (error) {
        addLog(`❌ Failed to initialize tiktoken: ${error}`);
        console.error('Tiktoken initialization error:', error);

        // Fallback: try without tiktoken
        addLog('🔄 Continuing without tiktoken - cost calculation will use estimated tokens');
      }
    };

    initializeTiktoken();
  }, [addLog]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTutorThinking && tutorThinkingStartTime) {
      interval = setInterval(() => {
        setElapsedTime((Date.now() - tutorThinkingStartTime) / 1000);
      }, 100);
    } else {
      setElapsedTime(0);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isTutorThinking, tutorThinkingStartTime]);

  // Handle environment change
  // Environment is stored in sessionStorage (per-tab) and sent in headers with each request
  const handleEnvironmentChange = (newEnvironment: 'local' | 'staging' | 'production') => {
    setEnvironment(newEnvironment);
    addLog(`✅ Switched to ${newEnvironment} environment (this tab only)`);

    // Map environment to database name for display
    const envMap = {
      'local': 'fermi-local',
      'staging': 'gofermi-dev',
      'production': 'gofermi-prod'
    };
    addLog(`📍 Will connect to database: ${envMap[newEnvironment]}`);
  };

  // Update the fetchPromptPackages function to not depend on selectedPromptPackage
  const fetchPromptPackages = useCallback(
    async (preserveSelection: boolean = true) => {
      setIsPromptPackagesLoading(true);
      try {
        addLog(`📦 Fetching prompt packages from ${SERVER_URL}`);
        const data: PromptPackage[] = await apiClient.get('/api/prompt-packages', PLAYGROUND);
        const sorted = sortPromptPackages(data);
        setPromptPackages(sorted);

        if (sorted.length === 0) {
          setSelectedPromptPackage(null);
          setSelectedLiveChatPromptPackage(null);
          setIsCreatingPromptPackage(false);
        } else {
          const currentKey = preserveSelection ? selectedPromptPackage?.key : null;
          const nextKey = currentKey && sorted.some((pkg) => pkg.key === currentKey)
            ? currentKey
            : sorted[0].key;
          const nextSelected = sorted.find((pkg) => pkg.key === nextKey) || null;
          setSelectedPromptPackage(nextSelected);
          
          // Also set the live chat prompt package if not already set
          if (!selectedLiveChatPromptPackage && sorted.length > 0) {
            setSelectedLiveChatPromptPackage(sorted[0]);
          }
          
          setIsCreatingPromptPackage(false);
        }

        addLog(`✅ Loaded ${data.length} prompt packages`);
      } catch (error) {
        addLog(`❌ Error fetching prompt packages: ${error}`);
        console.error('Error fetching prompt packages:', error);
        setPromptPackages([]);
        setSelectedPromptPackage(null);
        setSelectedLiveChatPromptPackage(null);
        setIsCreatingPromptPackage(false);
      } finally {
        setIsPromptPackagesLoading(false);
      }
    },
    [addLog, selectedPromptPackage?.key, selectedLiveChatPromptPackage]
  );

  useEffect(() => {
    fetchPromptPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once

  const formatConversationTimestamp = useCallback((message: ConversationMessage) => {
    const raw = message?.timestamp ?? message?.createdAt ?? message?.updatedAt;
    if (!raw) return '';

    const parsed = typeof raw === 'number'
      ? new Date(raw)
      : (() => {
          const value = String(raw);
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? new Date(Number(raw)) : date;
        })();

    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const handleAttemptLoad = useCallback(async () => {
    const trimmed = attemptIdInput.trim();
    if (!trimmed) {
      setReplayError('Please enter an attempt ID to load.');
      return;
    }

    setIsLoadingAttempt(true);
    setReplayError(null);
    setAwaitingReplayResponse(false);
    setCurrentReplayIndex(-1);
    addLog(`📥 Loading conversation for attempt ${trimmed}`);

    try {
      const data = await apiClient.get<{ conversation?: ConversationMessage[] }>(`/api/conversations/${trimmed}`, PLAYGROUND);
      const conversation = Array.isArray(data?.conversation)
        ? data.conversation
        : [];

      setAttemptConversation(conversation);

      const userTurns: ReplayTurn[] = conversation
        .map((msg, index) => ({ msg, index }))
        .filter(({ msg }) =>
          typeof msg?.type === 'string' &&
          msg.type.toLowerCase() === 'user' &&
          typeof msg.text === 'string' &&
          msg.text.trim().length > 0
        )
        .map(({ msg, index }) => ({
          id: msg._id ? String(msg._id) : `${trimmed}-${index}`,
          text: msg.text,
          questionUrl: msg.questionUrl || undefined,
          solutionUrl: msg.solutionUrl || undefined,
          boardText: msg.boardText ?? null,
          timestamp: msg.timestamp ?? msg.createdAt ?? msg.updatedAt,
          conversationIndex: index,
        }));

      setReplayQueue(userTurns);
      setLoadedAttemptId(trimmed);
      setMessages([]);
      setCurrentTranscript({});
      setCurrentReplayIndex(-1);
      setAwaitingReplayResponse(false);
      setQuestionImageUrl('');
      setSolutionImageUrl('');

      if (userTurns.length === 0) {
        setReplayError('No user messages were found for this attempt.');
        setQuestionImageUrl('');
        setSolutionImageUrl('');
        addLog(`⚠️ Attempt ${trimmed} does not contain replayable user turns`);
      } else {
        addLog(`✅ Loaded attempt ${trimmed} with ${userTurns.length} user turns`);
      }
    } catch (error) {
      console.error('Error loading attempt conversation:', error);
      const message = error instanceof Error ? error.message : 'Failed to load attempt';
      setReplayError(message);
      setAttemptConversation([]);
      setReplayQueue([]);
      setLoadedAttemptId(null);
      setQuestionImageUrl('');
      setSolutionImageUrl('');
      addLog(`❌ Failed to load attempt: ${message}`);
    } finally {
      setIsLoadingAttempt(false);
    }
  }, [attemptIdInput, addLog]);

  const sendReplayTurn = useCallback((index: number) => {
    if (awaitingReplayResponse) {
      addLog('⏳ Waiting for the tutor response before sending another turn.');
      return;
    }

    if (!isConnected) {
      const message = 'Connect to the tutor before sending a recorded turn.';
      setReplayError(message);
      addLog(`⚠️ ${message}`);
      return;
    }

    const turn = replayQueue[index];
    if (!turn) {
      addLog(`⚠️ Replay turn ${index + 1} is unavailable`);
      setAwaitingReplayResponse(false);
      return;
    }

    setReplayError(null);
    setCurrentReplayIndex(index);
    setQuestionImageUrl(turn.questionUrl || '');
    setSolutionImageUrl(turn.solutionUrl || '');
    setAwaitingReplayResponse(true);

    const userMessage: ChatMessage = {
      type: 'user',
      content: turn.text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      if (turn.questionUrl || turn.solutionUrl) {
        addLog(`📤 Sending recorded turn ${index + 1} with images`);
        sendTutorRequest(turn.text, turn.questionUrl || '', turn.solutionUrl || '');
      } else {
        addLog(`📤 Sending recorded turn ${index + 1}`);
        sendText(turn.text);
      }
    } catch (error) {
      console.error('Error sending replay turn:', error);
      addLog(`❌ Error sending replay turn: ${error}`);
      setAwaitingReplayResponse(false);
    }
  }, [awaitingReplayResponse, isConnected, replayQueue, addLog, sendTutorRequest, sendText]);

  const handleSelectPromptPackage = (key: string) => {
    if (isCreatingPromptPackage) {
      setIsCreatingPromptPackage(false);
    }
    const match = promptPackages.find((pkg) => pkg.key === key) || null;
    setSelectedPromptPackage(match);
  };

  const handleSavePromptPackage = async (key: string, updates: PromptPackageUpdate) => {
    try {
      addLog(`💾 Saving prompt package ${key}`);
      const updated: PromptPackage = await apiClient.put(`/api/prompt-packages/${key}`, PLAYGROUND, updates);
      setPromptPackages((prev) => {
        const next = prev.filter((pkg) => pkg.key !== updated.key);
        next.push(updated);
        return sortPromptPackages(next);
      });
      setSelectedPromptPackage(updated);
      setIsCreatingPromptPackage(false);
      addLog(`✅ Saved prompt package ${updated.key}`);
    } catch (error) {
      addLog(`❌ Error saving prompt package: ${error}`);
      console.error('Error saving prompt package:', error);
      throw error;
    }
  };

  const handleRefreshPromptPackages = async () => {
    setIsPromptPackagesRefreshing(true);
    try {
      await fetchPromptPackages();
    } finally {
      setIsPromptPackagesRefreshing(false);
    }
  };

  const handleStartCreatePromptPackage = () => {
    setSelectedPromptPackage(null);
    setIsCreatingPromptPackage(true);
  };

  const handleCancelCreatePromptPackage = () => {
    setIsCreatingPromptPackage(false);
    const prevKey = selectedPromptPackage?.key;
    if (prevKey && promptPackages.some((pkg) => pkg.key === prevKey)) {
      setSelectedPromptPackage(promptPackages.find((pkg) => pkg.key === prevKey) || null);
    } else if (promptPackages.length > 0) {
      setSelectedPromptPackage(promptPackages[0]);
    } else {
      setSelectedPromptPackage(null);
    }
  };

  const handleCreatePromptPackage = async (key: string, updates: PromptPackageUpdate) => {
    try {
      addLog(`🆕 Creating prompt package ${key}`);
      const created: PromptPackage = await apiClient.post('/api/prompt-packages', PLAYGROUND, { key, ...updates });
      setPromptPackages((prev) => sortPromptPackages([created, ...prev.filter((pkg) => pkg.key !== created.key)]));
      setSelectedPromptPackage(created);
      setIsCreatingPromptPackage(false);
      addLog(`✅ Created prompt package ${created.key}`);
    } catch (error) {
      addLog(`❌ Error creating prompt package: ${error}`);
      console.error('Error creating prompt package:', error);
      throw error;
    }
  };

  const handlePushPromptToProduction = async (key: string) => {
    try {
      addLog(`🚀 Pushing prompt package ${key} to production...`);
      const result = await apiClient.post<{ message: string }>(`/api/prompt-packages/${key}/push-to-prod`, PLAYGROUND);
      addLog(`✅ ${result.message}`);
      // Optionally refresh the prompt package list
      await fetchPromptPackages();
    } catch (error) {
      addLog(`❌ Error pushing to production: ${error}`);
      console.error('Error pushing prompt package to production:', error);
      throw error;
    }
  };

  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    try {
      switch (message.type) {
        case 'tutor_response':
          addLog('🎓 Processing tutor response');
          if (message.text_content || message.audio_content) {
            const tutorMessage: ChatMessage = {
              type: 'tutor',
              content: message.text_content || message.audio_content || '',
              text_content: message.text_content,
              audio_content: message.audio_content ?? null,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, tutorMessage]);
            addLog('📝 Text content updated');
            setAwaitingReplayResponse(prev => {
              if (!prev) return prev;
              addLog('✅ Tutor response received for replay turn');
              return false;
            });
          }
          break;

        case 'audio_chunk':
          addLog('🔊 Audio chunk received');
          if (message.audioData && message.audioData.audio) {
            const rawChunk = message.audioData.audio;
            let arrayBuffer: ArrayBuffer | null = null;

            if (rawChunk instanceof ArrayBuffer) {
              arrayBuffer = rawChunk;
            } else if (ArrayBuffer.isView(rawChunk)) {
              const view = new Uint8Array(
                rawChunk.buffer,
                rawChunk.byteOffset,
                rawChunk.byteLength
              );
              arrayBuffer = view.slice().buffer;
            } else if (typeof rawChunk === 'string') {
              try {
                const binaryString = atob(rawChunk);
                const buffer = new ArrayBuffer(binaryString.length);
                const view = new Uint8Array(buffer);
                for (let i = 0; i < binaryString.length; i += 1) {
                  view[i] = binaryString.charCodeAt(i);
                }
                arrayBuffer = buffer;
              } catch (error) {
                addLog(`❌ Failed to decode base64 audio chunk: ${error}`);
              }
            } else if (rawChunk?.type === 'Buffer' && Array.isArray(rawChunk.data)) {
              const uint8Array = new Uint8Array(rawChunk.data);
              arrayBuffer = uint8Array.buffer;
            }

            if (arrayBuffer) {
              playAudioChunk(arrayBuffer);
            } else {
              addLog('⚠️ Received audio chunk in unsupported format; skipping playback');
            }
          }
          break;

        case 'transcript':
          addLog(`📝 ${message.transcriptType || 'unknown'} transcript: ${message.isPartial ? 'partial' : 'complete'}`);

          setCurrentTranscript(prev => ({
            ...prev,
            [message.transcriptType!]: message.transcript!
          }));

          // For final assistant transcripts, add them to the conversation messages
          if (!message.isPartial && message.transcript && message.transcriptType === 'assistant') {
            const assistantMessage: ChatMessage = {
              type: 'assistant',
              content: message.transcript,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, assistantMessage]);
            addLog(`✅ Assistant message added to conversation: ${message.transcript.substring(0, 50)}...`);
            setAwaitingReplayResponse(prev => {
              if (!prev) return prev;
              addLog('✅ Assistant transcript received for replay turn');
              return false;
            });
          }

          if (!message.isPartial && message.transcriptType === 'supervisor') {
            setMessages(prev => {
              const updated = [...prev];
              let tutorIndex = -1;
              for (let i = updated.length - 1; i >= 0; i -= 1) {
                if (updated[i].type === 'tutor') {
                  tutorIndex = i;
                  break;
                }
              }

              if (tutorIndex >= 0) {
                updated[tutorIndex] = {
                  ...updated[tutorIndex],
                  content: message.transcript || updated[tutorIndex].content,
                  text_content: message.transcript ?? updated[tutorIndex].text_content,
                  audio_content: message.audio_content ?? updated[tutorIndex].audio_content ?? null
                };
              } else if (message.transcript || message.audio_content) {
                updated.push({
                  type: 'tutor',
                  content: message.transcript || message.audio_content || '',
                  text_content: message.transcript,
                  audio_content: message.audio_content ?? null,
                  timestamp: new Date()
                });
              }

              return updated;
            });
          }

          // For other final transcripts (like supervisor), just clear the live display
          if (!message.isPartial && message.transcript && message.transcriptType) {
            setCurrentTranscript(prev => ({
              ...prev,
              [message.transcriptType!]: ''
            }));
          }
          break;

        case 'user_transcript_final':
          addLog(`📝 Final user transcript: ${message.transcript}`);
          const transcriptMessage: ChatMessage = {
            type: 'user',
            content: message.transcript!,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, transcriptMessage]);
          setCurrentTranscript(prev => ({ ...prev, user: '' }));
          break;

        case 'cost_update':
            addLog('💰 Cost update received');
            break;

        case 'tool_call_start':
          addLog('🛠️ Tutor tool call started...');
          setIsTutorThinking(true);
          setTutorThinkingStartTime(Date.now());
          break;

        case 'tool_call_end':
          addLog('✅ Tutor tool call finished.');
          setIsTutorThinking(false);
          setTutorThinkingStartTime(null);
          break;

        case 'intent_classified':
          addLog(`🧠 Intent classified as: ${message.intent}`);
          const intentMessage: ChatMessage = {
            type: 'log',
            content: `Router classified intent as: **${message.intent}**`,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, intentMessage]);
          break;

        case 'error':
          addLog(`❌ Error received: ${message.message}`);
          console.error('WebSocket error:', message.message);
          break;

        default:
          addLog(`ℹ️ Unhandled message type: ${message.type}`);
      }
    } catch (error) {
      addLog(`❌ Error handling message: ${error}`);
      console.error('Error handling WebSocket message:', error);
    }
  }, [addLog, playAudioChunk]);

  useEffect(() => {
    addLog('🚀 App initialized');
  }, [addLog]);

  useEffect(() => {
    if (lastMessage) {
      handleWebSocketMessage(lastMessage);
    }
  }, [lastMessage, handleWebSocketMessage]);

  const handleConnect = async () => {
    try {
      addLog('🔗 Attempting to connect...');

      if (selectedLiveChatPromptPackage) {
        const realtimePromptData = {
          prompt: selectedLiveChatPromptPackage.promptPackage?.realtimePrompt || '',
          version: selectedLiveChatPromptPackage.metadata?.version || '1.0',
          filePath: selectedLiveChatPromptPackage.key || undefined,
        };
        
        const tutorPromptData = {
          prompt: selectedLiveChatPromptPackage.promptPackage?.systemText || '',
          version: selectedLiveChatPromptPackage.metadata?.version || '1.0',
          filePath: selectedLiveChatPromptPackage.key || undefined,
        };

        await connect(realtimePromptData, tutorPromptData);
        addLog('✅ Connection successful');
      } else {
        addLog('⚠️ Cannot connect: No prompt package selected.');
      }
    } catch (error) {
      addLog(`❌ Connection failed: ${error}`);
    }
  };

  const handleSendText = () => {
    if (!inputText.trim()) return;

    addLog(`📤 Sending text message: ${inputText.substring(0, 50)}...`);
    const userMessage: ChatMessage = {
      type: 'user',
      content: inputText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      if (questionImageUrl || solutionImageUrl) {
        addLog('🖼️ Sending tutor request with images');
        sendTutorRequest(inputText, questionImageUrl, solutionImageUrl);
      } else {
        sendText(inputText);
      }
    } catch (error) {
      addLog(`❌ Error sending message: ${error}`);
    }

    setInputText('');
  };

  const handleAudioRecord = async () => {
    try {
      if (isRecording) {
        addLog('⏹️ Stopping audio recording');
        stopAudioStream(); // Signal end of audio to backend
        stopRecording();
      } else {
        addLog('🎤 Starting audio recording');
        await startRecording((audioData) => {
          sendAudio(audioData, questionImageUrl, solutionImageUrl);
        });
      }
    } catch (error) {
      addLog(`❌ Audio recording error: ${error}`);
    }
  };

  const handleMuteToggle = () => {
    if (isMuted) {
      unmute();
    } else {
      mute();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const clearLogs = () => {
    setLogs([]);
    addLog('🧹 Logs cleared');
  };

  const handleGoogleLogin = () => {
    addLog('🔐 Initiating Google login...');
    // You can add any pre-login logic here
  };

  const handleLogout = () => {
    setUser(null);
    cookieUtils.delete(Auth.TOKEN);
    cookieUtils.deleteV2(Auth.TOKEN);
    logout();
  };

  const renderCostAnalysisTab = () => (
    <div className="tab-content">
      <CostAnalysis environment={environment} />
    </div>
  );

  const renderLiveChatTab = () => (
    <div className={`tab-content session-replay-tab ${loadedAttemptId ? 'attempt-loaded' : ''}`}>
      <div className="page-header">
        <h2>💬 Live Chat with Tutor</h2>
        <p className="page-description">Connect and start conversing with the AI tutor</p>

        <div className="connection-status">
          <div className="status-indicator-container">
            <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}></span>
            <span className="status-text">
              {isConnecting ? 'Connecting...' : isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div className="connection-controls">
            {!isConnected && !isConnecting && (
              <button onClick={handleConnect} className="primary-btn" disabled={!selectedLiveChatPromptPackage}>
                🔗 Connect
              </button>
            )}
            {isConnected && (
              <button onClick={disconnect} className="secondary-btn">
                🔌 Disconnect
              </button>
            )}
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="secondary-btn"
              title="Toggle debug logs"
            >
              📊 {showLogs ? 'Hide' : 'Show'} Logs
            </button>
          </div>
        </div>
      </div>

      {showLogs && (
        <div className="logs-panel">
          <div className="logs-header">
            <h3>📊 Debug Logs</h3>
            <button onClick={clearLogs} className="secondary-btn">Clear</button>
          </div>
          <div className="logs-content">
            {logs.map((log, index) => (
              <div key={index} className="log-entry">{log}</div>
            ))}
          </div>
        </div>
      )}

      <div className="live-chat-layout">
        <div className="chat-main">
          <div className="replay-section">
            <div className="replay-controls">
              <h3>🧪 Session Replay</h3>
              
              {/* Prompt Package Selector - Moved here */}
              <div className="prompt-package-selector">
                <label htmlFor="live-chat-prompt-package">Prompt Package:</label>
                <select
                  id="live-chat-prompt-package"
                  value={selectedLiveChatPromptPackage?.key || ''}
                  onChange={(e) => {
                    const selected = promptPackages.find(pkg => pkg.key === e.target.value) || null;
                    setSelectedLiveChatPromptPackage(selected);
                    addLog(`📦 Selected prompt package: ${selected?.name || 'None'}`);
                  }}
                  className="prompt-package-select"
                  disabled={isPromptPackagesLoading}
                >
                  <option value="">
                    {isPromptPackagesLoading ? 'Loading packages...' : 'Select a prompt package...'}
                  </option>
                  {promptPackages
                    .filter(pkg => pkg.isActive)
                    .map(pkg => (
                      <option key={pkg.key} value={pkg.key}>
                        {pkg.name} (v{pkg.metadata?.version || '1.0'}) - {pkg.metadata?.stage || 'dev'}
                      </option>
                    ))
                  }
                </select>
                {selectedLiveChatPromptPackage && (
                  <div className="prompt-version-bubbles">
                    <span className="prompt-bubble package">
                      {selectedLiveChatPromptPackage.name} v{selectedLiveChatPromptPackage.metadata?.version}
                    </span>
                  </div>
                )}
                {promptPackages.length === 0 && !isPromptPackagesLoading && (
                  <div className="prompt-package-empty">
                    No active prompt packages found. Create one in the Prompt Packages tab.
                  </div>
                )}
              </div>

              <div className="attempt-input-row">
                <input
                  id="attempt-id"
                  type="text"
                  value={attemptIdInput}
                  onChange={(e) => setAttemptIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAttemptLoad();
                    }
                  }}
                  placeholder="Enter attempt ID"
                  className="attempt-input"
                  disabled={isLoadingAttempt}
                />
                <button
                  onClick={handleAttemptLoad}
                  className="secondary-btn"
                  disabled={isLoadingAttempt}
                >
                  {isLoadingAttempt ? 'Loading...' : 'Load Attempt'}
                </button>
              </div>
              <div className="replay-status">
                <span><strong>Attempt:</strong> {loadedAttemptId || '—'}</span>
                <span><strong>Turns:</strong> {replayQueue.length}</span>
                <span><strong>Status:</strong> {isLoadingAttempt ? 'Loading...' : replayStatusLabel}</span>
                {currentReplayIndex >= 0 && (
                  <span><strong>Last sent turn:</strong> {currentReplayIndex + 1}</span>
                )}
              </div>
              {replayError && (
                <div className="replay-error">
                  {replayError}
                </div>
              )}
            </div>

            <div className="replay-images">
              <h4>📷 Reference Images</h4>
              <div className="replay-image-grid">
                {questionImageUrl ? (
                  <figure className="replay-image-card">
                    <span>Question</span>
                    <img src={questionImageUrl} alt="Question" className="replay-image" />
                  </figure>
                ) : (
                  <div className="replay-placeholder">Question image unavailable</div>
                )}
                {solutionImageUrl ? (
                  <figure className="replay-image-card">
                    <span>Solution</span>
                    <img src={solutionImageUrl} alt="Solution" className="replay-image" />
                  </figure>
                ) : (
                  <div className="replay-placeholder">Solution image unavailable</div>
                )}
              </div>
            </div>
          </div>

          <div className="replay-body">
            <div className="chat-conversation">
              <div className="chat-container">
                <div className="messages">
                  {messages.map((message, index) => (
                    <div key={index} className={`message ${message.type}`}>
                      <div className="message-header">
                        <span className="sender">
                          {message.type === 'user' ? '👤 You' :
                            message.type === 'tutor' ? '🎓 Tutor' :
                            message.type === 'log' ? '🧠 System' : '🤖 Assistant'}
                        </span>
                        <span className="timestamp">{formatTime(message.timestamp)}</span>
                      </div>
                      <div className="message-content">
                        {message.type === 'tutor' ? (
                          <div className="tutor-message-content">
                            {message.audio_content && (
                              <div className="tutor-section">
                                <div className="tutor-section-label">Audio Response</div>
                                <MarkdownRenderer content={message.audio_content} />
                              </div>
                            )}
                            {message.text_content && (
                              <div className="tutor-section">
                                <div className="tutor-section-label">Board Content</div>
                                <MarkdownRenderer content={message.text_content} />
                              </div>
                            )}
                            {!message.audio_content && !message.text_content && (
                              <MarkdownRenderer content={message.content} />
                            )}
                          </div>
                        ) : (message.type === 'assistant' || message.type === 'log') ? (
                          <MarkdownRenderer content={message.content} />
                        ) : (
                          message.content
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Real-time transcript display */}
                  {currentTranscript.user && (
                    <div className="message user-transcript">
                      <span className="transcript-label">🎤 You:</span>
                      <span className="transcript-text">{currentTranscript.user}</span>
                    </div>
                  )}
                  {currentTranscript.assistant && (
                    <div className="message assistant-transcript">
                      <span className="transcript-label">🤖 Assistant:</span>
                      <span className="transcript-text">{currentTranscript.assistant}</span>
                    </div>
                  )}
                  {currentTranscript.tutor && (
                    <div className="message tutor-transcript">
                      <span className="transcript-label">🎓 Tutor:</span>
                      <span className="transcript-text">{currentTranscript.tutor}</span>
                    </div>
                  )}
                  {currentTranscript.supervisor && (
                    <div className="message supervisor-transcript">
                      <span className="transcript-label">🎓 Supervisor:</span>
                      <span className="transcript-text">{currentTranscript.supervisor}</span>
                    </div>
                  )}
                </div>

                <div className="input-area">
                  {isTutorThinking && (
                    <div className="thinking-indicator">
                      🎓 Tutor is thinking... ({elapsedTime.toFixed(1)}s)
                    </div>
                  )}
                  <div className="image-url-inputs">
                    <label className="image-url-field">
                      <span>Question Image URL</span>
                      <input
                        type="text"
                        value={questionImageUrl}
                        onChange={(e) => setQuestionImageUrl(e.target.value)}
                        placeholder="https://..."
                      />
                    </label>
                    <label className="image-url-field">
                      <span>Solution Image URL</span>
                      <input
                        type="text"
                        value={solutionImageUrl}
                        onChange={(e) => setSolutionImageUrl(e.target.value)}
                        placeholder="https://..."
                      />
                    </label>
                  </div>
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={isRecording ? 'Recording... Speak now!' : 'Type your message or press Record'}
                    className="text-input"
                    rows={3}
                    disabled={isRecording}
                  />

                  <div className="controls">
                    <button
                      onClick={handleAudioRecord}
                      className={`audio-btn ${isRecording ? 'recording' : ''}`}
                      disabled={!isConnected || isPlaying}
                    >
                      {isRecording ? '⏹️ Stop Recording' : '🎤 Record'}
                    </button>

                    <button
                      onClick={handleMuteToggle}
                      className="secondary-btn"
                      disabled={!isRecording}
                      title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
                    >
                      {isMuted ? '🔇 Unmute' : '🔈 Mute'}
                    </button>

                    {isPlaying && (
                      <div className="audio-playing-indicator">
                        🔊 Playing audio...
                      </div>
                    )}

                    <button
                      onClick={handleSendText}
                      className="primary-btn"
                      disabled={!isConnected || !inputText.trim() || isRecording}
                    >
                      📤 Send Message
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <aside className="original-conversation-panel">
              <div className="original-transcript-header">
                <h3>🗂️ Original Transcript</h3>
              </div>
              <div className="original-messages">
                {attemptConversation.length === 0 ? (
                  <div className="original-empty">
                    Load an attempt to preview its recorded conversation.
                  </div>
                ) : (
                  attemptConversation
                    .map((message, index) => ({ message, index }))
                    .filter(({ message }) => {
                      const type = typeof message.type === 'string' ? message.type.toLowerCase() : '';
                      return type === 'user' || type === 'assistant' || type === 'supervisor' || type === 'tutor';
                    })
                    .map(({ message, index }) => {
                      const type = typeof message.type === 'string' ? message.type.toLowerCase() : 'unknown';
                      const roleLabel = (() => {
                        switch (type) {
                          case 'user':
                            return 'User';
                          case 'assistant':
                            return 'Assistant';
                          case 'tutor':
                          case 'supervisor':
                            return 'Supervisor';
                          default:
                            return 'Message';
                        }
                      })();
                      const replayIndex = replayIndexByConversation.get(index);
                      const isReplayTurn = typeof replayIndex === 'number';
                      const isActive = isReplayTurn && replayIndex === currentReplayIndex;
                      const classes = [
                        'original-message',
                        `role-${type}`,
                        isActive ? 'is-active' : '',
                      ].filter(Boolean).join(' ');
                      const isPendingTurn = awaitingReplayResponse && isReplayTurn && replayIndex === currentReplayIndex;
                      const sendButtonLabel = isPendingTurn ? 'Sending...' : 'Send';
                      return (
                        <div key={message._id || `${index}-${type}`} className={classes}>
                          <div className="original-message-header">
                            <span className="original-role">{roleLabel}</span>
                            <span className="original-timestamp">{formatConversationTimestamp(message)}</span>
                          </div>
                          <div className="original-text">
                            {message.text && message.text.trim().length
                              ? <MarkdownRenderer content={message.text} />
                              : 'No text available for this message.'}
                          </div>
                          {message.boardText && message.boardText.trim().length > 0 && (
                            <div className="original-board">
                              <MarkdownRenderer content={message.boardText} />
                            </div>
                          )}
                          {(message.questionUrl || message.solutionUrl) && (
                            <div className="original-attachments">
                              {message.questionUrl && (
                                <figure>
                                  <span>Question</span>
                                  <img src={message.questionUrl} alt="Question" />
                                </figure>
                              )}
                              {message.solutionUrl && (
                                <figure>
                                  <span>Solution</span>
                                  <img src={message.solutionUrl} alt="Solution" />
                                </figure>
                              )}
                            </div>
                          )}
                          {type === 'user' && isReplayTurn && (
                            <div className="original-actions">
                              <button
                                className="secondary-btn send-turn-btn"
                                onClick={() => sendReplayTurn(replayIndex)}
                                disabled={disableRecordedSend}
                                title={disableRecordedSend ? 'Connect to the tutor and wait for any pending response.' : 'Send this recorded turn to the tutor'}
                              >
                                {sendButtonLabel}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                )}
              </div>
            </aside>
          </div>

        </div>
      </div>
    </div>
  );

  const renderPromptPackagesTab = () => (
    <div className="tab-content">
      <PromptPackages
        prompts={promptPackages}
        selectedPrompt={selectedPromptPackage}
        onSelectPrompt={handleSelectPromptPackage}
        onSavePrompt={handleSavePromptPackage}
        onCreatePrompt={handleCreatePromptPackage}
        onPushToProduction={handlePushPromptToProduction}
        onRefreshPrompt={handleRefreshPromptPackages}
        onStartCreatePrompt={handleStartCreatePromptPackage}
        onCancelCreatePrompt={handleCancelCreatePromptPackage}
        isLoading={isPromptPackagesLoading}
        isRefreshing={isPromptPackagesRefreshing}
        isCreating={isCreatingPromptPackage}
      />
    </div>
  );

  const renderTrackUsersTab = () => (
    <div className="tab-content">
      <TrackUserActivity environment={environment} />
    </div>
  );

  const renderImplicitCheckerReplayTab = () => (
    <div className="tab-content">
      <ImplicitCheckerReplay environment={environment} />
    </div>
  );

  if (!user) {
    return (
      <div className="app">
        <GoogleLogin
          loginUrl={googleLoginUrl}
          onLoginClick={handleGoogleLogin}
          isOpen={true}
          onClose={() => {}}
          disabled={false}
          title="Sign in to continue"
          description="Please sign in with your Google account to access this feature."
        />
      </div>
    );
  }

  // Show access denied page if user is not authorized
  if (accessDenied) {
    return (
      <div className="app" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center' 
      }}>
        <div style={{
          maxWidth: '400px',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚫</div>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#333',
            marginBottom: '15px' 
          }}>
            Access Denied
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#666',
            marginBottom: '10px',
            lineHeight: '1.5'
          }}>
            {accessDeniedMessage || 'Your email is not authorized to access this application.'}
          </p>
          <p style={{ 
            fontSize: '14px', 
            color: '#888',
            marginBottom: '25px'
          }}>
            Logged in as: <strong>{user?.email}</strong>
          </p>
          <button
            onClick={() => {
              // Set user to null first to prevent showing home page
              setUser(null);
              // logout() clears cookie and redirects to '/'
              logout();
            }}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '12px 30px',
              fontSize: '16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h1>🎓 Fermi Tutor Playground</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <select
                value={environment}
                onChange={(e) => handleEnvironmentChange(e.target.value as 'local' | 'staging' | 'production')}
                style={{
                  padding: '5px 10px',
                  fontSize: '14px',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  backgroundColor: environment === 'local' ? '#fff3cd' : environment === 'staging' ? '#d1ecf1' : '#d4edda',
                  cursor: 'pointer'
                }}
                title="Environment selection is per-tab. Each browser tab can use a different environment."
              >
                <option value="local">Local</option>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
              <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                (per-tab)
              </span>
            </div>
          </div>
          <nav className="tabs">
            <button
              className={`tab ${activeTab === 'live-chat' ? 'active' : ''}`}
              onClick={() => handleTabChange('live-chat')}
            >
              ⏯️ Session Replay
            </button>
            <button
              className={`tab ${activeTab === 'cost-analysis' ? 'active' : ''}`}
              onClick={() => handleTabChange('cost-analysis')}
            >
              💰 Cost Analysis
            </button>
            <button
              className={`tab ${activeTab === 'track-users' ? 'active' : ''}`}
              onClick={() => handleTabChange('track-users')}
            >
              👤 Track User Activity
            </button>
            <button
              className={`tab ${activeTab === 'prompt-packages' ? 'active' : ''}`}
              onClick={() => handleTabChange('prompt-packages')}
            >
              🛠️ Prompt Packages
            </button>
            <button
              className={`tab ${activeTab === 'implicit-checker-replay' ? 'active' : ''}`}
              onClick={() => handleTabChange('implicit-checker-replay')}
            >
              🔍 Implicit Checker Replay
            </button>
          </nav>
          <div className="auth-section">
            {user ? (
              <div className="user-info">
                {user.picture && (
                  <img src={user.picture} alt="Profile" className="user-avatar" />
                )}
                <span className="user-name">{user.name}</span>
                <button onClick={handleLogout} className="logout-btn">
                  Logout
                </button>
              </div>
            ) : (
              <></>
            )}
             <GoogleLogin
                loginUrl={googleLoginUrl}
                onLoginClick={handleGoogleLogin}
                isOpen={!user}
                onClose={() => {}}
                disabled={false}
                title="Sign in to continue"
                description="Please sign in with your Google account to access this feature."
              />
          </div>
        </div>
      </header>

      <main className="app-main">
        {activeTab === 'cost-analysis'
          ? renderCostAnalysisTab()
          : activeTab === 'track-users'
          ? renderTrackUsersTab()
          : activeTab === 'prompt-packages'
          ? renderPromptPackagesTab()
          : activeTab === 'implicit-checker-replay'
          ? renderImplicitCheckerReplayTab()
          : renderLiveChatTab()}
      </main>
    </div>
  );
};

export default App;